import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useSideBarContext } from '../context/SidebarContext';
import { AuthContext } from '../context/AuthContext';
import { useDispatch, useSelector } from 'react-redux';
import { LogoutApi } from '../redux/actions/Auth/LogoutAction';
import Swal from 'sweetalert2';
import { UpdateSearchTerm } from '../redux/slices/Filters/SearchSlice';
import { ChangePasswordApi } from '../redux/actions/Profile/ChangePasswordAction';
import { showErrorToast, showSuccessToast } from '../helpers/SweetAlert';
import { changePasswordStateReset } from '../redux/slices/Profile/ChangePasswordSlice';
import { updateProfileStateReset } from '../redux/slices/Profile/UpdateProfileSlice';
import { UpdateProfileAPI } from '../redux/actions/Profile/UpdateProfileAction';

const Header = () => {

    const backend_url = useMemo(() => {
        return process.env.REACT_APP_MODE === "development"
            ? process.env.REACT_APP_BACKEND_DEVELOPMENT_URL
            : process.env.REACT_APP_BACKEND_PRODUCTION_URL;
    }, []);

    const navigate = useNavigate();
    const dispatch = useDispatch();
    const location = useLocation();

    const cartItems = useSelector((state) => state.cart.items); // Adjust path based on your actual slice
    const { fetchProfile } = useContext(AuthContext);

    const cartCount = cartItems?.length || 0;
    const { logout } = useContext(AuthContext);
    const { toggleSidebar } = useSideBarContext();
    const [searchTerm, setSearchTerm] = useState('');

    const [password, setPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');

    const [isPasswordShown, setIsPasswordShown] = useState(false);
    const [isNewPasswordShown, setIsNewPasswordShown] = useState(false);
    const [isConfirmPasswordShown, setIsConfirmPasswordShown] = useState(false);

    const [fullName, setFullName] = useState('');
    const [selectedProfileImage, setSelectedProfileImage] = useState(null);
    const [profileImage, setProfileImage] = useState(null);

    const [validationErrors, setValidationErrors] = useState('');

    const { response: changePasswordResponse, loading: changePasswordLoading, error: changePasswordError } = useSelector((state) => state.changePassword)
    const { response: profileResponse, loading: profileLoading } = useSelector((state) => state.getProfile);
    const { response: updateProfileResponse } = useSelector((state) => state.updateProfile);

    const { response } = useSelector((state) => state.logout);

    // const handleLogout = async () => {
    //     navigate('/')
    // }
    // const handleSearch = () => {
    //     // Dispatch the search term to update the global state
    //     dispatch(UpdateSearchTerm(searchTerm));
    // };

    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchTerm(value);
        dispatch(UpdateSearchTerm(value));

        // If input is cleared, you can reset the search term in the global state
        if (value === '') {
            dispatch(UpdateSearchTerm('')); // or clear the search filter
        }
    };

    const handleLogout = (event) => {
        event.preventDefault();
        Swal.fire({
            title: 'Are you sure?',
            text: "You want to log out!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, log out!',
            cancelButtonText: 'No, cancel',
        }).then((result) => {
            if (result.isConfirmed) {
                // Call the logout API
                // dispatch(LogoutApi(localStorage?.getItem('authToken')));
                dispatch(LogoutApi())
            } else if (result.dismiss === Swal.DismissReason.cancel) {
                Swal.fire(
                    'Cancelled',
                    'You are still logged in :)',
                    'info'
                );
            }
        });
    };

    const validateForm = () => {
        const newErrors = {};

        if (!password) newErrors.password = true;
        if (!newPassword.trim()) newErrors.newPassword = true;
        if (!confirmPassword.trim()) newErrors.confirmPassword = true;

        setValidationErrors(newErrors);

        return Object.keys(newErrors).length === 0; // true if no errors
    };

    const togglePasswordVisibility = useCallback((field) => {
        if (field === "password") {
            setIsPasswordShown((prev) => !prev);
        } else if (field === "newPassword") {
            setIsNewPasswordShown((prev) => !prev);
        } else if (field === "confirmPassword") {
            setIsConfirmPasswordShown((prev) => !prev);
        }
    }, []);

    const handleChangePassword = async () => {
        if (!validateForm()) return;

        try {
            const data = { password: password, newPassword: newPassword, confirmPassword: confirmPassword };

            await dispatch(ChangePasswordApi(data));
        } catch (error) {
            console.log(error, "error in change password")
        }
    }

    const handleSave = useCallback(async () => {
        try {
            if (fullName?.trim() === '') {
                showErrorToast("Name cannot be empty");
                return;
            }

            const formData = new FormData();

            formData.append('name', fullName);
            formData.append('image', selectedProfileImage);
            await dispatch(UpdateProfileAPI(formData));
        } catch (error) {
            console.error('Error updating profile', error);
        }
    }, [fullName, profileImage, dispatch]);

    const handleImageUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            setProfileImage(URL.createObjectURL(file));
            setSelectedProfileImage(file)
        }
    };

    useEffect(() => {
        if (response?.status === true) {
            logout();
            Swal.fire(
                'Logged out!',
                'You have been successfully logged out.',
                'success'
            );
        }
    }, [response, logout, dispatch]);

    useEffect(() => {
        setSearchTerm('');  // Clear the search input when tab changes
        dispatch(UpdateSearchTerm(''));  // Clear global state as well
    }, [location]);

    useEffect(() => {
        if (changePasswordResponse?.status === true) {
            showSuccessToast(changePasswordResponse?.message)
            dispatch(changePasswordStateReset());
            setPassword('');
            setConfirmPassword('');
            setNewPassword('');
            setValidationErrors('');
            const modalElement = document.getElementById('changePasswordModal');
            const modal = window.bootstrap.Modal.getInstance(modalElement);
            if (modal) {
                modal.hide();
            } else {
                const newModal = new window.bootstrap.Modal(modalElement);
                newModal.hide();
            }

        }
        if (changePasswordError) {
            showErrorToast(changePasswordError?.data);
            dispatch(changePasswordStateReset());
            setPassword('');
            setConfirmPassword('');
            setNewPassword('');
            setValidationErrors('')
        }
    }, [changePasswordResponse, changePasswordError]);

    useEffect(() => {
        const changePasswordModal = document.getElementById('changePasswordModal');
        const handleClose = () => {
            // Reset password form fields
            setPassword('');
            setNewPassword('');
            setConfirmPassword('');
            setValidationErrors('');
        };

        if (changePasswordModal) {
            changePasswordModal.addEventListener('hidden.bs.modal', handleClose);
        }
        return () => {
            if (changePasswordModal) {
                changePasswordModal.removeEventListener('hidden.bs.modal', handleClose);
            }
        };
    }, []);


    useEffect(() => {
        const profileViewModal = document.getElementById('profileModal');
        const handleClose = () => {
            setFullName(profileResponse?.data?.name)
        };

        if (profileViewModal) {
            profileViewModal.addEventListener('hidden.bs.modal', handleClose);
        }
        return () => {
            if (profileViewModal) {
                profileViewModal.removeEventListener('hidden.bs.modal', handleClose);
            }
        };
    }, []);

    useEffect(() => {
        if (profileResponse) {
            setFullName(profileResponse?.data?.name)
            setSelectedProfileImage((prev) => prev || profileResponse?.data?.image);
        }
        else {
            setFullName('')
            setSelectedProfileImage(null)
        }
    }, [profileResponse]);

    useEffect(() => {
        if (updateProfileResponse?.status === true) {
            dispatch(updateProfileStateReset());
            showSuccessToast('Profile updated successfully!');
            const modalElement = document.getElementById('profileModal');
            const modal = window.bootstrap.Modal.getInstance(modalElement);
            if (modal) {
                modal.hide();
            } else {
                const newModal = new window.bootstrap.Modal(modalElement);
                newModal.hide();
            }
            fetchProfile();
        }

    }, [updateProfileResponse])
    return (
        <>
            <header>
                <nav className="navbar-expand-sm navbar-light">
                    <div className="container">
                        <div className="row justify-content-between align-items-center">
                            <div className="col-6">
                                <div className="header-flex">
                                    <button className="opnBtn" type="button" style={{ display: 'inline-block' }} onClick={toggleSidebar}>
                                        <i className="fa-solid fa-bars"></i>
                                    </button>
                                    {(location.pathname !== '/admin-dashboard') && (location.pathname !== '/admin-dashboard/cart') && (location.pathname !== '/admin-dashboard/send-notifications') && (location.pathname !== '/admin-dashboard/add-menu') && (location.pathname !== '//admin-dashboard/menu-management') && (location.pathname !== '/admin-dashboard/queries-management') &&
                                        <div className='header_search search-field position-relative'>
                                            <input type='search' className='form-control' placeholder='Search something.....' value={searchTerm}
                                                onChange={handleSearchChange}
                                                onKeyDown={(e) => {
                                                    const value = e.target.value;
                                                    // Prevent space if the input is still empty or only contains spaces
                                                    if (e.key === ' ' && value.trim().length === 0) {
                                                        e.preventDefault();
                                                    }
                                                }} />
                                            <span className='search-icon'></span>
                                        </div>
                                    }
                                </div>
                            </div>

                            <div className="col-6">
                                <div className="header-menu">
                                    <ul className="navbar-nav justify-content-end ms-auto mb-0 navsbar">
                                        <li className='nav-item'>
                                            <div className="navbar_prof-wrapper d-flex justify-content-end align-items-center">
                                                <div className="header_notification-blk">
                                                    <button className="header_notification-btn " onClick={() => navigate('/admin-dashboard/cart')}>
                                                        <img src='/assets/icons/cart-icon.svg' alt='icon' />
                                                        {cartCount > 0 && (
                                                            <span className="cart-count-badge">{cartCount}</span>
                                                        )}
                                                    </button>
                                                </div>
                                                <div className="header_notification-blk">
                                                    <button className="header_notification-btn " onClick={() => navigate('/admin-dashboard/notification')}>
                                                        <img src='/assets/icons/bell-icon.svg' alt='icon' />
                                                    </button>
                                                </div>
                                                <div className="header_profile-blk position-relative">
                                                    <button type="button" className="header_profile-btn dropdown-toggle" data-bs-toggle="dropdown" data-bs-display="static" aria-expanded="false">
                                                        <span className="header_user_profile">
                                                            <img src={`${backend_url}${profileResponse?.data?.image}`} alt="Profile" />
                                                        </span>
                                                        <span className="d-none d-sm-flex flex-column align-items-start">Hi,{profileResponse?.data?.name}</span>
                                                    </button>
                                                    <ul className="dropdown-menu dropdown-menu-end dropdown-menu-sm-end">
                                                        <li>
                                                            <a className="dropdown-item text-capitalize" data-bs-toggle='modal' data-bs-target='#profileModal'>
                                                                <span className="dropdown_item-icon">
                                                                    <svg width={16} height={18} viewBox="0 0 16 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                        <path d="M8.00013 9.62499C5.35846 9.62499 3.20846 7.47499 3.20846 4.83332C3.20846 2.19166 5.35846 0.0416565 8.00013 0.0416565C10.6418 0.0416565 12.7918 2.19166 12.7918 4.83332C12.7918 7.47499 10.6418 9.62499 8.00013 9.62499ZM8.00013 1.29166C6.05013 1.29166 4.45846 2.88332 4.45846 4.83332C4.45846 6.78332 6.05013 8.37499 8.00013 8.37499C9.95013 8.37499 11.5418 6.78332 11.5418 4.83332C11.5418 2.88332 9.95013 1.29166 8.00013 1.29166ZM15.1585 17.9583C14.8168 17.9583 14.5335 17.675 14.5335 17.3333C14.5335 14.4583 11.6001 12.125 8.00013 12.125C4.40013 12.125 1.4668 14.4583 1.4668 17.3333C1.4668 17.675 1.18346 17.9583 0.841797 17.9583C0.50013 17.9583 0.216797 17.675 0.216797 17.3333C0.216797 13.775 3.70846 10.875 8.00013 10.875C12.2918 10.875 15.7835 13.775 15.7835 17.3333C15.7835 17.675 15.5001 17.9583 15.1585 17.9583Z" fill="black" />
                                                                    </svg>
                                                                </span>
                                                                <span>My Profile</span>
                                                            </a>
                                                        </li>
                                                        <li>
                                                            <button className="dropdown-item text-capitalize" data-bs-toggle='modal' data-bs-target='#changePasswordModal'>
                                                                <span className="dropdown_item-icon">
                                                                    <svg xmlns="http://www.w3.org/2000/svg" width={24} height={24} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-lock-open-icon lucide-lock-open"><rect width={18} height={11} x={3} y={11} rx={2} ry={2} /><path d="M7 11V7a5 5 0 0 1 9.9-1" /></svg>
                                                                </span>
                                                                <span>Change Password</span>
                                                            </button>
                                                        </li>
                                                        <li>
                                                            <Link to="#" className="dropdown-item text-capitalize" onClick={handleLogout}>
                                                                <span className="dropdown_item-icon">
                                                                    <svg width={20} height={20} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                        <path d="M19.3735 9.37536H11.457C11.112 9.37536 10.832 9.09537 10.832 8.75038C10.832 8.40539 11.112 8.1254 11.457 8.1254H19.3735C19.7185 8.1254 19.9985 8.40539 19.9985 8.75038C19.9985 9.09537 19.7185 9.37536 19.3735 9.37536Z" fill="black" />
                                                                        <path d="M16.2495 12.5004C16.1674 12.5006 16.0861 12.4845 16.0103 12.453C15.9344 12.4215 15.8656 12.3753 15.8077 12.3171C15.5636 12.0728 15.5636 11.677 15.8077 11.4329L18.4911 8.74971L15.8077 6.06639C15.5636 5.82226 15.5636 5.42645 15.8077 5.18232C16.052 4.93803 16.4478 4.93803 16.692 5.18232L19.8169 8.30722C20.061 8.55136 20.061 8.94716 19.8169 9.1913L16.692 12.3162C16.6341 12.3747 16.5652 12.4211 16.4892 12.4527C16.4133 12.4843 16.3318 12.5005 16.2495 12.5004ZM6.66651 20.0001C6.48816 20.0001 6.31894 19.9751 6.14985 19.9226L1.13493 18.2518C0.452525 18.0135 0 17.3777 0 16.667V1.66743C0 0.748279 0.747516 0.000762939 1.66667 0.000762939C1.84486 0.000762939 2.01412 0.0257622 2.18333 0.0782605L7.1981 1.74907C7.88061 1.98738 8.33302 2.62322 8.33302 3.33394V18.3335C8.33302 19.2526 7.58566 20.0001 6.66651 20.0001ZM1.66667 1.25072C1.43749 1.25072 1.24996 1.43822 1.24996 1.66743V16.667C1.24996 16.8444 1.3691 17.0093 1.53909 17.0686L6.53054 18.7319C6.5664 18.7435 6.61311 18.7502 6.66651 18.7502C6.89568 18.7502 7.08306 18.5626 7.08306 18.3335V3.33394C7.08306 3.15648 6.96392 2.99153 6.79393 2.93235L1.80248 1.26904C1.76662 1.25744 1.71991 1.25072 1.66667 1.25072Z" fill="black" />
                                                                        <path d="M12.7072 6.66715C12.3622 6.66715 12.0822 6.38716 12.0822 6.04217V2.29229C12.0822 1.71809 11.6149 1.2506 11.0407 1.2506H1.666C1.32101 1.2506 1.04102 0.97061 1.04102 0.625621C1.04102 0.280632 1.32101 0.000640869 1.666 0.000640869H11.0407C12.3048 0.000640869 13.3322 1.02815 13.3322 2.29229V6.04217C13.3322 6.38716 13.0522 6.66715 12.7072 6.66715ZM11.0407 17.5001H7.70737C7.36238 17.5001 7.08239 17.2201 7.08239 16.8751C7.08239 16.5301 7.36238 16.2501 7.70737 16.2501H11.0407C11.6149 16.2501 12.0822 15.7826 12.0822 15.2084V11.4586C12.0822 11.1136 12.3622 10.8336 12.7072 10.8336C13.0522 10.8336 13.3322 11.1136 13.3322 11.4586V15.2084C13.3322 16.4726 12.3049 17.5001 11.0407 17.5001Z" fill="black" />
                                                                    </svg>
                                                                </span>
                                                                <span>Logout</span>
                                                            </Link>
                                                        </li>
                                                    </ul>
                                                </div>
                                            </div>
                                        </li>
                                        {/* <li className="nav-item">
                                            <Link to="#" className="top-nav-link logout-link" onClick={handleLogout}>
                                                <img src="/assets/images/logout-icon.svg" alt="Logout" />
                                            </Link>
                                        </li> */}
                                        {/*<li className="nav-item">
                                            <div className="profile-imgs">
                                                <Link to="/admin-dashboard/admin-profile" className="nav-link profile-link">
                                                    <img src={profileImage || profileImageNew} alt="Profile" />
                                                </Link> 
                                            </div>
                                        </li> */}
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                </nav>
            </header >

            {/* Profile Modal */}
            <div className="modal fade" id="profileModal" tabindex="-1" aria-labelledby="profileLabel" aria-hidden="true">
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content">
                        <div className="modal-body detail-modal-body">
                            <div className="common-box-body">
                                <div className='update_profile'>

                                    {(selectedProfileImage || profileImage) && (
                                        profileImage ?
                                            <img
                                                src={profileImage}
                                                alt="Profile"
                                                className='image_preview'
                                            /> :
                                            <img src={`${backend_url}${selectedProfileImage}`} alt={fullName} className='image_preview' />
                                    )}

                                    {/* <img src='/assets/images/beckham.jpg' alt="User" /> */}
                                    <div className='upld_img_field'>
                                        <input type="file" className='d-none' id='profileImg' accept="image/*" onChange={handleImageUpload} />
                                        <label htmlFor='profileImg'>
                                            <img src='/assets/icons/edit-pencil.svg' alt="Edit" />
                                        </label>
                                    </div>
                                </div>
                                <div className="common-modal-form mt-4">
                                    <div className="common-modal-field">
                                        <label for="profileName" className='mb-2'>Change name</label>
                                        <div className="p-relative">
                                            <input type="text" className="form-control" id="profileName" placeholder='Enter name' value={fullName}
                                                onChange={(e) => {
                                                    const value = e.target.value;
                                                    if (value.length === 1 && value === ' ') return;
                                                    setFullName(value);
                                                }} />
                                        </div>
                                    </div>
                                </div>
                                <div className="modal_btn d-flex justify-content-center gap-2 p-0 mt-4">
                                    <button className="transparent-btn fs-14 py-2" data-bs-dismiss="modal" aria-label="Close">Cancel</button>
                                    <button className="primary-btn fs-14 py-2" onClick={handleSave}>Save</button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Change Password Modal */}
            <div className="modal fade" id="changePasswordModal" tabindex="-1" aria-labelledby="changePasswordLabel" aria-hidden="true">
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content">
                        <div className="modal-body detail-modal-body">
                            <div className="common-box-body">
                                <h2>Change Password</h2>
                                <div className="common-modal-form mt-4">
                                    <div className="common-modal-field">
                                        <label for="oldPassword">Old Password</label>
                                        <div className="p-relative">
                                            <input type={isPasswordShown ? "text" : "password"} className={`form-control ${validationErrors.password ? 'is-invalid' : ''}`} placeholder='Enter old password' value={password} id="oldPassword" onChange={(e) => {
                                                const value = e.target.value;
                                                if (value.length === 1 && value === ' ') return;
                                                setPassword(value);
                                                if (validationErrors.password) {
                                                    setValidationErrors(prev => ({ ...prev, password: false }));
                                                }
                                            }} />
                                            {!validationErrors.password && <span id="eye" onClick={() => togglePasswordVisibility('password')}>
                                                {isPasswordShown ? (
                                                    <i className="fa-solid fa-eye" id="eyeIcon"></i>
                                                ) : (
                                                    <i className="fa-solid fa-eye-slash" id="eyeIcon"></i>
                                                )}
                                            </span>}
                                        </div>
                                        {/* {errors.password && <div style={{ color: 'red' }}>{errors.password}</div>} */}
                                    </div>
                                    <div className="common-modal-field">
                                        <label for="newPassword">New Password</label>
                                        <div className="p-relative">
                                            <input type={isNewPasswordShown ? "text" : "password"} className={`form-control ${validationErrors.newPassword ? 'is-invalid' : ''}`} placeholder='Enter new password' id="newPassword" value={newPassword} onChange={(e) => {
                                                const value = e.target.value;
                                                if (value.length === 1 && value === ' ') return;
                                                setNewPassword(value);
                                                if (validationErrors.newPassword) {
                                                    setValidationErrors(prev => ({ ...prev, newPassword: false }));
                                                }
                                            }} />
                                            {!validationErrors.newPassword && <span id="eye" onClick={() => togglePasswordVisibility('newPassword')}>
                                                {isNewPasswordShown ? (
                                                    <i className="fa-solid fa-eye" id="eyeIcon"></i>
                                                ) : (
                                                    <i className="fa-solid fa-eye-slash" id="eyeIcon"></i>
                                                )}
                                            </span>}
                                        </div>
                                        {/* {errors.newPassword && <div style={{ color: 'red' }}>{errors.newPassword}</div>} */}
                                    </div>
                                    <div className="common-modal-field">
                                        <label for="confirmPassword">Confirm Password</label>
                                        <div className="p-relative">
                                            <input type={isConfirmPasswordShown ? "text" : "password"} className={`form-control ${validationErrors.confirmPassword ? 'is-invalid' : ''}`} placeholder='Enter confirm password' value={confirmPassword} id="confirmPassword" onChange={(e) => {
                                                const value = e.target.value;
                                                if (value.length === 1 && value === ' ') return;
                                                setConfirmPassword(value);
                                                if (validationErrors.confirmPassword) {
                                                    setValidationErrors(prev => ({ ...prev, confirmPassword: false }));
                                                }
                                            }} />
                                            {!validationErrors.confirmPassword && <span id="eye" onClick={() => togglePasswordVisibility('confirmPassword')}>
                                                {isConfirmPasswordShown ? (
                                                    <i className="fa-solid fa-eye" id="eyeIcon"></i>
                                                ) : (
                                                    <i className="fa-solid fa-eye-slash" id="eyeIcon"></i>
                                                )}
                                            </span>}
                                        </div>
                                        {/* {errors.confirmPassword && <div style={{ color: 'red' }}>{errors.confirmPassword}</div>} */}
                                    </div>
                                </div>
                                <div className="modal_btn d-flex justify-content-center gap-2 p-0 mt-4">
                                    <button className="transparent-btn fs-14 py-2" data-bs-dismiss="modal" aria-label="Close">Cancel</button>
                                    <button className="primary-btn fs-14 py-2" onClick={handleChangePassword}>Save</button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}

export default Header